/**
 * Error Handler for WebSocket Notifications
 *
 * Phase 4: Error Handling
 *
 * Features:
 * - Offline queue for failed operations
 * - Retry logic with exponential backoff
 * - User-facing error messages
 * - Graceful degradation
 *
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Error types
 */
export const ErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  RATE_LIMIT_ERROR: 'RATE_LIMIT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  LOW: 'LOW',       // Informational, no action needed
  MEDIUM: 'MEDIUM', // Warning, may need attention
  HIGH: 'HIGH',     // Error, needs attention
  CRITICAL: 'CRITICAL' // Critical, immediate action required
};

/**
 * Retry strategies
 */
export const RetryStrategy = {
  EXPONENTIAL_BACKOFF: 'EXPONENTIAL_BACKOFF',
  LINEAR_BACKOFF: 'LINEAR_BACKOFF',
  FIXED_DELAY: 'FIXED_DELAY',
  NO_RETRY: 'NO_RETRY'
};

/**
 * Error Handler Class
 *
 * Handles errors with retry logic, offline queue, and user notifications
 */
export class ErrorHandler {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries !== undefined ? options.maxRetries : 3;
    this.baseDelay = options.baseDelay !== undefined ? options.baseDelay : 1000; // 1 second
    this.maxDelay = options.maxDelay !== undefined ? options.maxDelay : 30000; // 30 seconds
    this.retryStrategy = options.retryStrategy || RetryStrategy.EXPONENTIAL_BACKOFF;
    this.enableUserNotifications = options.enableUserNotifications !== false;
    this.enableOfflineQueue = options.enableOfflineQueue !== false;

    // Offline queue
    this.offlineQueue = [];
    this.maxQueueSize = options.maxQueueSize !== undefined ? options.maxQueueSize : 100;

    // Retry tracking
    this.retryAttempts = new Map(); // operationId -> attempt count
    this.failedOperations = new Map(); // operationId -> error details

    // Statistics
    this.stats = {
      totalErrors: 0,
      errorsByType: {},
      errorsBySeverity: {},
      totalRetries: 0,
      successfulRetries: 0,
      failedRetries: 0,
      queuedOperations: 0,
      processedFromQueue: 0,
      lastErrorTime: null
    };

    // Initialize error type counters
    Object.values(ErrorType).forEach(type => {
      this.stats.errorsByType[type] = 0;
    });

    // Initialize severity counters
    Object.values(ErrorSeverity).forEach(severity => {
      this.stats.errorsBySeverity[severity] = 0;
    });

    console.log('🛡️ Error handler initialized');
  }

  /**
   * Handle an error
   *
   * @param {Error} error - The error object
   * @param {Object} context - Error context (operation, data, etc.)
   * @returns {Object} Error handling result
   */
  handleError(error, context = {}) {
    const errorType = this.classifyError(error);
    const severity = this.determineSeverity(errorType, context);

    // Update statistics
    this.stats.totalErrors++;
    this.stats.errorsByType[errorType]++;
    this.stats.errorsBySeverity[severity]++;
    this.stats.lastErrorTime = Date.now();

    // Log error
    this.logError(error, errorType, severity, context);

    // Determine if retry is needed
    const shouldRetry = this.shouldRetry(errorType, context);

    if (shouldRetry) {
      const retryResult = this.retryOperation(context);
      // Add error information to retry result
      return retryResult.then(result => ({
        ...result,
        error,
        errorType,
        severity
      }));
    }

    // Queue for offline processing if applicable
    if (this.shouldQueue(errorType, context)) {
      const queueResult = this.queueOperation(context);
      // Add error information to queue result
      return {
        ...queueResult,
        error,
        errorType,
        severity
      };
    }

    // Show user notification if enabled
    if (this.enableUserNotifications) {
      this.notifyUser(error, errorType, severity, context);
    }

    // Store failed operation
    if (context.operationId) {
      this.failedOperations.set(context.operationId, {
        error,
        errorType,
        severity,
        context,
        timestamp: Date.now()
      });
    }

    return {
      success: false,
      error,
      errorType,
      severity,
      retried: false,
      queued: false
    };
  }

  /**
   * Classify error type
   */
  classifyError(error) {
    if (!error) {
      return ErrorType.UNKNOWN_ERROR;
    }

    const message = error.message?.toLowerCase() || '';

    // Network errors
    if (message.includes('network') || message.includes('fetch') ||
        message.includes('connection') || error.name === 'NetworkError') {
      return ErrorType.NETWORK_ERROR;
    }

    // Timeout errors
    if (message.includes('timeout') || error.name === 'TimeoutError') {
      return ErrorType.TIMEOUT_ERROR;
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid')) {
      return ErrorType.VALIDATION_ERROR;
    }

    // Authentication errors
    if (message.includes('auth') || message.includes('unauthorized') ||
        message.includes('forbidden') || error.status === 401 || error.status === 403) {
      return ErrorType.AUTHENTICATION_ERROR;
    }

    // Rate limit errors
    if (message.includes('rate limit') || message.includes('too many') ||
        error.status === 429) {
      return ErrorType.RATE_LIMIT_ERROR;
    }

    // Server errors
    if (error.status >= 500 || message.includes('server error')) {
      return ErrorType.SERVER_ERROR;
    }

    return ErrorType.UNKNOWN_ERROR;
  }

  /**
   * Determine error severity
   */
  determineSeverity(errorType, context = {}) {
    // Critical errors
    if (errorType === ErrorType.AUTHENTICATION_ERROR) {
      return ErrorSeverity.CRITICAL;
    }

    // High severity errors
    if (errorType === ErrorType.SERVER_ERROR) {
      return ErrorSeverity.HIGH;
    }

    // Medium severity errors
    if (errorType === ErrorType.NETWORK_ERROR ||
        errorType === ErrorType.TIMEOUT_ERROR ||
        errorType === ErrorType.RATE_LIMIT_ERROR) {
      return ErrorSeverity.MEDIUM;
    }

    // Low severity errors (validation errors, unknown errors)
    if (errorType === ErrorType.VALIDATION_ERROR) {
      return ErrorSeverity.LOW;
    }

    // Low severity for unknown errors
    return ErrorSeverity.LOW;
  }

  /**
   * Determine if operation should be retried
   */
  shouldRetry(errorType, context = {}) {
    // Don't retry validation or authentication errors
    if (errorType === ErrorType.VALIDATION_ERROR ||
        errorType === ErrorType.AUTHENTICATION_ERROR) {
      return false;
    }

    // Check if max retries reached
    const operationId = context.operationId;
    if (operationId) {
      const attempts = this.retryAttempts.get(operationId) || 0;
      if (attempts >= this.maxRetries) {
        return false;
      }
    }

    // Retry network, timeout, and server errors
    return errorType === ErrorType.NETWORK_ERROR ||
           errorType === ErrorType.TIMEOUT_ERROR ||
           errorType === ErrorType.SERVER_ERROR ||
           errorType === ErrorType.RATE_LIMIT_ERROR;
  }

  /**
   * Determine if operation should be queued
   */
  shouldQueue(errorType, context = {}) {
    if (!this.enableOfflineQueue) {
      return false;
    }

    // Queue network errors when offline
    if (errorType === ErrorType.NETWORK_ERROR) {
      return true;
    }

    return false;
  }

  /**
   * Retry an operation
   */
  async retryOperation(context) {
    const operationId = context.operationId || `retry-${Date.now()}`;
    const attempts = this.retryAttempts.get(operationId) || 0;

    // Update retry count
    this.retryAttempts.set(operationId, attempts + 1);
    this.stats.totalRetries++;

    // Calculate delay
    const delay = this.calculateRetryDelay(attempts);

    console.log(`🔄 Retrying operation ${operationId} (attempt ${attempts + 1}/${this.maxRetries}) after ${delay}ms`);

    // Wait before retry
    await new Promise(resolve => setTimeout(resolve, delay));

    try {
      // Execute retry function if provided
      if (context.retryFn && typeof context.retryFn === 'function') {
        const result = await context.retryFn();

        // Success
        this.stats.successfulRetries++;
        this.retryAttempts.delete(operationId);

        console.log(`✅ Retry successful for operation ${operationId}`);

        return {
          success: true,
          result,
          retried: true,
          attempts: attempts + 1
        };
      }
    } catch (error) {
      console.error(`❌ Retry failed for operation ${operationId}:`, error);
      this.stats.failedRetries++;

      // Try again if not max retries
      if (attempts + 1 < this.maxRetries) {
        return this.retryOperation(context);
      }
    }

    return {
      success: false,
      retried: true,
      attempts: attempts + 1,
      maxRetriesReached: true
    };
  }

  /**
   * Calculate retry delay based on strategy
   */
  calculateRetryDelay(attemptNumber) {
    switch (this.retryStrategy) {
      case RetryStrategy.EXPONENTIAL_BACKOFF:
        // Exponential backoff: delay = baseDelay * 2^attemptNumber
        const exponentialDelay = this.baseDelay * Math.pow(2, attemptNumber);
        // Add jitter (±25%)
        const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
        return Math.min(exponentialDelay + jitter, this.maxDelay);

      case RetryStrategy.LINEAR_BACKOFF:
        // Linear backoff: delay = baseDelay * (attemptNumber + 1)
        return Math.min(this.baseDelay * (attemptNumber + 1), this.maxDelay);

      case RetryStrategy.FIXED_DELAY:
        // Fixed delay
        return this.baseDelay;

      case RetryStrategy.NO_RETRY:
      default:
        return 0;
    }
  }

  /**
   * Queue an operation for offline processing
   */
  queueOperation(context) {
    if (!this.enableOfflineQueue) {
      return { success: false, queued: false };
    }

    // Check queue size
    if (this.offlineQueue.length >= this.maxQueueSize) {
      console.warn('⚠️ Offline queue is full, removing oldest operation');
      this.offlineQueue.shift();
    }

    const queuedOperation = {
      id: context.operationId || `queued-${Date.now()}`,
      context,
      timestamp: Date.now()
    };

    this.offlineQueue.push(queuedOperation);
    this.stats.queuedOperations++;

    console.log(`📥 Queued operation ${queuedOperation.id} (queue size: ${this.offlineQueue.length})`);

    return {
      success: true,
      queued: true,
      queueSize: this.offlineQueue.length
    };
  }

  /**
   * Process offline queue
   */
  async processOfflineQueue() {
    if (this.offlineQueue.length === 0) {
      console.log('📭 Offline queue is empty');
      return { processed: 0, failed: 0 };
    }

    console.log(`📤 Processing offline queue (${this.offlineQueue.length} operations)`);

    let processed = 0;
    let failed = 0;

    // Process queue in order
    while (this.offlineQueue.length > 0) {
      const operation = this.offlineQueue.shift();

      try {
        if (operation.context.retryFn && typeof operation.context.retryFn === 'function') {
          await operation.context.retryFn();
          processed++;
          this.stats.processedFromQueue++;
          console.log(`✅ Processed queued operation ${operation.id}`);
        }
      } catch (error) {
        console.error(`❌ Failed to process queued operation ${operation.id}:`, error);
        failed++;

        // Track retry count for this operation
        const retryCount = operation.retryCount || 0;

        // Re-queue if not too old (max 1 hour) and hasn't been retried too many times
        const age = Date.now() - operation.timestamp;
        const maxRetries = 3; // Maximum 3 retries for queued operations

        if (age < 3600000 && retryCount < maxRetries) { // 1 hour and max 3 retries
          operation.retryCount = retryCount + 1;
          this.offlineQueue.push(operation);
          console.log(`🔄 Re-queued operation ${operation.id} (retry ${operation.retryCount}/${maxRetries})`);
        } else {
          if (age >= 3600000) {
            console.log(`⏰ Operation ${operation.id} expired (age: ${Math.round(age / 1000)}s)`);
          } else {
            console.log(`❌ Operation ${operation.id} exceeded max retries (${maxRetries})`);
          }
        }
      }
    }

    console.log(`📊 Offline queue processed: ${processed} successful, ${failed} failed`);

    return { processed, failed };
  }

  /**
   * Clear offline queue
   */
  clearOfflineQueue() {
    const size = this.offlineQueue.length;
    this.offlineQueue = [];
    console.log(`🗑️ Cleared offline queue (${size} operations removed)`);
    return size;
  }

  /**
   * Get offline queue size
   */
  getOfflineQueueSize() {
    return this.offlineQueue.length;
  }

  /**
   * Log error
   */
  logError(error, errorType, severity, context) {
    const emoji = this.getSeverityEmoji(severity);
    const message = `${emoji} [${errorType}] ${error.message || 'Unknown error'}`;

    if (severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH) {
      console.error(message, { error, context });
    } else if (severity === ErrorSeverity.MEDIUM) {
      console.warn(message, { error, context });
    } else {
      console.log(message, { error, context });
    }
  }

  /**
   * Get emoji for severity level
   */
  getSeverityEmoji(severity) {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return '🚨';
      case ErrorSeverity.HIGH:
        return '❌';
      case ErrorSeverity.MEDIUM:
        return '⚠️';
      case ErrorSeverity.LOW:
      default:
        return 'ℹ️';
    }
  }

  /**
   * Notify user about error
   */
  notifyUser(error, errorType, severity, context) {
    // Only notify for medium and above severity
    if (severity === ErrorSeverity.LOW) {
      return;
    }

    const message = this.getUserFriendlyMessage(errorType, context);

    // This will be integrated with react-toastify in WebSocketService
    console.log(`📢 User notification: ${message}`);

    return {
      message,
      severity,
      errorType
    };
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(errorType, context = {}) {
    const lang = context.language || 'en';

    const messages = {
      en: {
        [ErrorType.NETWORK_ERROR]: 'Connection lost. Trying to reconnect...',
        [ErrorType.TIMEOUT_ERROR]: 'Request timed out. Please try again.',
        [ErrorType.VALIDATION_ERROR]: 'Invalid data. Please check your input.',
        [ErrorType.SERVER_ERROR]: 'Server error. Please try again later.',
        [ErrorType.AUTHENTICATION_ERROR]: 'Authentication failed. Please log in again.',
        [ErrorType.RATE_LIMIT_ERROR]: 'Too many requests. Please wait a moment.',
        [ErrorType.UNKNOWN_ERROR]: 'An error occurred. Please try again.'
      },
      fa: {
        [ErrorType.NETWORK_ERROR]: 'اتصال قطع شد. در حال تلاش برای اتصال مجدد...',
        [ErrorType.TIMEOUT_ERROR]: 'زمان درخواست به پایان رسید. لطفا دوباره تلاش کنید.',
        [ErrorType.VALIDATION_ERROR]: 'داده نامعتبر. لطفا ورودی خود را بررسی کنید.',
        [ErrorType.SERVER_ERROR]: 'خطای سرور. لطفا بعدا دوباره تلاش کنید.',
        [ErrorType.AUTHENTICATION_ERROR]: 'احراز هویت ناموفق. لطفا دوباره وارد شوید.',
        [ErrorType.RATE_LIMIT_ERROR]: 'درخواست‌های زیاد. لطفا کمی صبر کنید.',
        [ErrorType.UNKNOWN_ERROR]: 'خطایی رخ داد. لطفا دوباره تلاش کنید.'
      }
    };

    return messages[lang]?.[errorType] || messages.en[errorType];
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      ...this.stats,
      offlineQueueSize: this.offlineQueue.length,
      activeRetries: this.retryAttempts.size,
      failedOperationsCount: this.failedOperations.size
    };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalErrors: 0,
      errorsByType: {},
      errorsBySeverity: {},
      totalRetries: 0,
      successfulRetries: 0,
      failedRetries: 0,
      queuedOperations: 0,
      processedFromQueue: 0,
      lastErrorTime: null
    };

    // Re-initialize counters
    Object.values(ErrorType).forEach(type => {
      this.stats.errorsByType[type] = 0;
    });

    Object.values(ErrorSeverity).forEach(severity => {
      this.stats.errorsBySeverity[severity] = 0;
    });

    console.log('📊 Error handler statistics reset');
  }

  /**
   * Clear retry attempts
   */
  clearRetryAttempts() {
    const size = this.retryAttempts.size;
    this.retryAttempts.clear();
    console.log(`🗑️ Cleared retry attempts (${size} operations)`);
    return size;
  }

  /**
   * Clear failed operations
   */
  clearFailedOperations() {
    const size = this.failedOperations.size;
    this.failedOperations.clear();
    console.log(`🗑️ Cleared failed operations (${size} operations)`);
    return size;
  }

  /**
   * Get failed operations
   */
  getFailedOperations() {
    return Array.from(this.failedOperations.entries()).map(([id, details]) => ({
      id,
      ...details
    }));
  }
}
